"use client";

// Sprint 2 — Reorganización de configuración
import { useState } from "react";
import { css, cx } from "styled-system/css";
import { cf } from "@/components/brand/sfturnos-styles";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { useAuth } from "@/providers/AuthProvider";
import { useToast } from "@/hooks/useToast";

type Cfg = {
  sendConfirmEmail: boolean;
  sendCancelEmail: boolean;
  notifyNewBookingEmail: boolean;
};

export function NotifSettingsForm({ initial }: { initial: Cfg }) {
  const { refresh } = useAuth();
  const toast = useToast();
  const [cfg, setCfg] = useState(initial);
  const [saving, setSaving] = useState(false);

  async function save(next: Cfg) {
    setCfg(next);
    setSaving(true);
    const r = await fetchWithSessionSync(
      "/api/dashboard/notif-config",
      {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(next),
      },
      refresh,
    );
    setSaving(false);
    if (r.ok) toast.success("Guardado");
    else toast.error("No se pudo guardar.");
  }

  function toggle<K extends keyof Cfg>(key: K, value: boolean) {
    void save({ ...cfg, [key]: value });
  }

  const row = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "4",
    py: "4",
    borderBottomWidth: "1px",
    borderColor: "#e2e8f0",
    _last: { borderBottomWidth: "0" },
  });

  const label = css({ fontSize: "sm", color: "#0f172a", flex: "1" });
  const hint = css({ fontSize: "xs", color: "#64748b", mt: "1" });

  return (
    <div className={cx(cf.dashSurface, css({ p: { base: "4", md: "6" } }))}>
      <p className={css({ fontSize: "xs", color: "#64748b", mb: "4" })}>
        {saving ? "Guardando…" : "Los cambios se guardan al tocar cada interruptor."}
      </p>
      <div>
        <div className={row}>
          <div>
            <p className={label}>Correo al confirmar un turno</p>
            <p className={hint}>El cliente recibe confirmación por email.</p>
          </div>
          <Toggle checked={cfg.sendConfirmEmail} onChange={(v) => toggle("sendConfirmEmail", v)} />
        </div>
        <div className={row}>
          <div>
            <p className={label}>Correo si se cancela</p>
            <p className={hint}>Aviso al cliente cuando cancelás un turno.</p>
          </div>
          <Toggle checked={cfg.sendCancelEmail} onChange={(v) => toggle("sendCancelEmail", v)} />
        </div>
        <div className={row}>
          <div>
            <p className={label}>Correo cuando te reservan</p>
            <p className={hint}>Recibís un aviso en tu correo de cuenta al ingresar una reserva nueva.</p>
          </div>
          <Toggle checked={cfg.notifyNewBookingEmail} onChange={(v) => toggle("notifyNewBookingEmail", v)} />
        </div>
      </div>
    </div>
  );
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={css({
        w: "11",
        h: "6",
        rounded: "full",
        position: "relative",
        flexShrink: 0,
        transition: "background 0.2s",
        bg: checked ? "rgba(129, 140, 248, 0.9)" : "#e2e8f0",
        borderWidth: "1px",
        borderColor: checked ? "rgba(99,102,241,0.4)" : "#cbd5e1",
        cursor: "pointer",
      })}
    >
      <span
        className={css({
          position: "absolute",
          top: "3px",
          left: checked ? "calc(100% - 22px)" : "3px",
          w: "18px",
          h: "18px",
          rounded: "full",
          bg: "white",
          transition: "left 0.2s",
          boxShadow: "sm",
        })}
      />
    </button>
  );
}
